import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Archive,
  Copy,
  Eye,
  Image,
  Pencil,
  Plus,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import CatalogExplorer from "../components/CatalogExplorer";
import CatalogRecordEditor from "../components/CatalogRecordEditor";
import DashboardHeader from "../components/DashboardHeader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const catalogApi = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;

const typeLabels = {
  catalog_category: "Catalog Categories",
  game: "Games",
  game_variant: "Game Variants / Regions",
  language: "Languages",
  region: "Regions",
  publisher: "Publishers",
  era: "Eras / Series / Blocks",
  set: "Sets",
  subset: "Subsets",
  promotional_set: "Promotional Sets",
  set_symbol: "Set Symbols",
  rarity: "Rarities",
  card_type: "Card Types",
  card_finish: "Card Finishes",
  classification: "Additional Classifications",
};

const singularTypeLabels = {
  catalog_category: "Catalog Category",
  card_finish: "Card Finish",
  card_type: "Card Type",
  classification: "Additional Classification",
  era: "Era / Series / Block",
  game: "Game",
  game_variant: "Game Variant / Region",
  language: "Language",
  promotional_set: "Promotional Set",
  publisher: "Publisher",
  rarity: "Rarity",
  region: "Region",
  set: "Set",
  set_symbol: "Set Symbol",
  subset: "Subset",
};

const requestConfig = { withCredentials: true };

export default function CatalogManagementPage({ user, onLogout }) {
  const [recordType, setRecordType] = useState("catalog_category");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [sort, setSort] = useState("updated_desc");
  const [page, setPage] = useState(1);
  const [legacyMode, setLegacyMode] = useState(false);
  const [parentOptions, setParentOptions] = useState({});
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [quickCreateContext, setQuickCreateContext] = useState(null);
  const [quickCreated, setQuickCreated] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchResultIndex, setSearchResultIndex] = useState(0);
  const [explorerRefreshKey, setExplorerRefreshKey] = useState(0);
  const requestSequence = useRef(0);

  const loadParents = useCallback(async () => {
    const parentTypes = [
      "catalog_category",
      "game",
      "game_variant",
      "era",
      "set",
    ];
    const responses = await Promise.all(
      parentTypes.map((type) =>
        axios.get(`${catalogApi}/records`, {
          ...requestConfig,
          params: { record_type: type, limit: 100, sort: "name_asc" },
        }),
      ),
    );
    setParentOptions({
      category_id: responses[0].data.items,
      game_id: responses[1].data.items,
      variant_id: responses[2].data.items,
      era_id: responses[3].data.items,
      set_id: responses[4].data.items,
    });
  }, []);

  const loadRecords = useCallback(async () => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    if (legacyMode) {
      const response = await axios.get(`${catalogApi}/legacy-sets`, {
        ...requestConfig,
        params: { search: search || undefined },
      });
      if (requestId === requestSequence.current) {
        setRecords(response.data);
        setTotal(response.data.length);
      }
      return;
    }
    const response = await axios.get(`${catalogApi}/records`, {
      ...requestConfig,
      params: {
        record_type: recordType,
        search: search || undefined,
        status_filter: statusFilter || undefined,
        active: activeFilter === "" ? undefined : activeFilter === "active",
        sort,
        page,
        limit: 25,
      },
    });
    if (requestId === requestSequence.current) {
      setRecords(response.data.items);
      setTotal(response.data.total);
    }
  }, [activeFilter, legacyMode, page, recordType, search, sort, statusFilter]);

  useEffect(() => {
    Promise.all([loadParents(), loadRecords()]).catch(() => {
      toast.error("Cardfolio couldn’t load Catalog Management.");
    });
  }, [loadParents, loadRecords]);

  useEffect(() => {
    if (user.role !== "owner") {
      return;
    }
    axios
      .get(`${catalogApi}/permissions`, requestConfig)
      .then((response) => {
        setPermissions(response.data);
      })
      .catch(() => setPermissions(null));
  }, [user.role]);

  const openCreate = (nextRecordType) => {
    const typeToCreate =
      typeof nextRecordType === "string" ? nextRecordType : recordType;
    setLegacyMode(false);
    setRecordType(typeToCreate);
    setEditingRecord(null);
    setEditorOpen(true);
  };

  const openEdit = (record) => {
    setLegacyMode(record.record_id?.startsWith("legacy:"));
    setRecordType(record.record_type || "set");
    setEditingRecord(record);
    setEditorOpen(true);
  };

  const saveRecord = async (payload, addAnother) => {
    let response;
    if (editingRecord?.record_id?.startsWith("legacy:")) {
      const legacySetId = editingRecord.record_id.replace("legacy:", "");
      response = await axios.put(
        `${catalogApi}/legacy-sets/${legacySetId}`,
        payload,
        requestConfig,
      );
    } else if (editingRecord) {
      response = await axios.put(
        `${catalogApi}/records/${editingRecord.record_id}`,
        payload,
        requestConfig,
      );
    } else {
      response = await axios.post(
        `${catalogApi}/records`,
        payload,
        requestConfig,
      );
    }
    toast.success(
      payload.status === "published"
        ? "Catalog record published."
        : "Catalog draft saved.",
    );
    await Promise.all([loadParents(), loadRecords()]);
    setExplorerRefreshKey((current) => current + 1);
    if (!addAnother) {
      setEditorOpen(false);
    }
    return response.data;
  };

  const saveQuickRecord = async (payload) => {
    const response = await axios.post(
      `${catalogApi}/records`,
      payload,
      requestConfig,
    );
    await Promise.all([loadParents(), loadRecords()]);
    setExplorerRefreshKey((current) => current + 1);
    setQuickCreated({ field: quickCreateContext.field, record: response.data });
    setQuickCreateContext(null);
    toast.success("Related catalog record created and linked.");
    return response.data;
  };

  const duplicate = async (record) => {
    await axios.post(
      `${catalogApi}/records/${record.record_id}/duplicate`,
      {},
      requestConfig,
    );
    toast.success("Draft duplicate created.");
    await loadRecords();
    setExplorerRefreshKey((current) => current + 1);
  };

  const changeStatus = async (record, nextStatus) => {
    if (record.record_id.startsWith("legacy:")) {
      const legacySetId = record.record_id.replace("legacy:", "");
      await axios.patch(
        `${catalogApi}/legacy-sets/${legacySetId}/status`,
        { status: nextStatus },
        requestConfig,
      );
      toast.success(`Frozen Set Library record ${nextStatus}.`);
      await loadRecords();
      return;
    }
    const usageResponse = await axios.get(
      `${catalogApi}/records/${record.record_id}/usage`,
      requestConfig,
    );
    const usageTotal = Object.values(usageResponse.data).reduce(
      (sum, value) => sum + value,
      0,
    );
    if (nextStatus === "archived" && usageTotal) {
      toast.message(
        `This record remains connected to ${usageTotal} related record(s). ` +
          "It will be archived, not deleted.",
      );
    }
    await axios.patch(
      `${catalogApi}/records/${record.record_id}/status`,
      { status: nextStatus },
      requestConfig,
    );
    toast.success(`Catalog record ${nextStatus}.`);
    await loadRecords();
    setExplorerRefreshKey((current) => current + 1);
  };

  const viewDetail = async (record) => {
    const historyResponse = await axios.get(
      `${catalogApi}/records/${record.record_id}/history`,
      requestConfig,
    );
    setHistory(historyResponse.data);
    setDetailRecord(record);
  };

  const updatePermissions = async (admin, permission, enabled) => {
    const current = new Set(admin.catalog_permissions || []);
    enabled ? current.add(permission) : current.delete(permission);
    await axios.put(
      `${catalogApi}/permissions/${admin.user_id}`,
      { permissions: [...current] },
      requestConfig,
    );
    const response = await axios.get(
      `${catalogApi}/permissions`,
      requestConfig,
    );
    setPermissions(response.data);
    toast.success("Admin catalog permissions updated.");
  };

  const chooseType = (nextType) => {
    setLegacyMode(false);
    setRecordType(nextType);
    setPage(1);
    setSearch("");
    setSearchResultIndex(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSearchResultIndex((current) =>
        Math.min(current + 1, records.length - 1),
      );
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSearchResultIndex((current) => Math.max(current - 1, 0));
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (search.trim()) {
        setRecentSearches((current) =>
          [
            search.trim(),
            ...current.filter((query) => query !== search.trim()),
          ].slice(0, 4),
        );
      }
      if (records[searchResultIndex]) {
        openEdit(records[searchResultIndex]);
      }
    }
  };

  return (
    <div
      className="dashboard-shell catalog-shell"
      data-testid="catalog-management-page"
    >
      <DashboardHeader user={user} onLogout={onLogout} activePage="Catalog" />
      <main className="catalog-main">
        <header className="catalog-page-header">
          <div>
            <p className="eyebrow">Admin · Catalog Management</p>
            <h1>Build the reference catalog by hand.</h1>
            <p>
              Start with a record type, then use the explorer to build
              relationships without code or imports.
            </p>
          </div>
          <button
            className="admin-primary-button"
            type="button"
            onClick={openCreate}
            data-testid="catalog-add-record-button"
          >
            <Plus size={17} /> Add {singularTypeLabels[recordType] || "record"}
          </button>
        </header>

        <div className="catalog-layout">
          <div
            className="catalog-explorer-column"
            data-testid="catalog-type-navigation"
          >
            <CatalogExplorer
              activeRecord={editingRecord}
              onCreate={openCreate}
              onSelect={openEdit}
              onTypeSelect={chooseType}
              refreshKey={explorerRefreshKey}
            />
            <button
              type="button"
              className={`catalog-frozen-library-link ${legacyMode ? "is-active" : ""}`}
              onClick={() => {
                setLegacyMode(true);
                setPage(1);
                setEditingRecord(null);
              }}
              data-testid="catalog-legacy-sets-button"
            >
              Frozen Set Library <span>154</span>
            </button>
          </div>

          <section className="catalog-content">
            <div className="catalog-toolbar" data-testid="catalog-list-filters">
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                  setSearchResultIndex(0);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search name, code, or alias"
                data-testid="catalog-search-input"
              />
              {!legacyMode && (
                <select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(1);
                  }}
                  data-testid="catalog-status-filter"
                >
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              )}
              {!legacyMode && (
                <select
                  value={activeFilter}
                  onChange={(event) => {
                    setActiveFilter(event.target.value);
                    setPage(1);
                  }}
                  data-testid="catalog-active-filter"
                >
                  <option value="">Active & inactive</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              )}
              {!legacyMode && (
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  data-testid="catalog-sort-select"
                >
                  <option value="updated_desc">Recently updated</option>
                  <option value="name_asc">Name A–Z</option>
                  <option value="order_asc">Display order</option>
                </select>
              )}
            </div>
            {recentSearches.length > 0 && (
              <div
                className="catalog-recent-searches"
                data-testid="catalog-recent-searches"
              >
                <span>Recent</span>
                {recentSearches.map((query) => (
                  <button
                    key={query}
                    onClick={() => {
                      setSearch(query);
                      setPage(1);
                    }}
                    type="button"
                    data-testid={`catalog-recent-search-${query}`}
                  >
                    {query}
                  </button>
                ))}
              </div>
            )}

            <div className="catalog-list-meta">
              <span>{total} records</span>
              <span>
                {legacyMode
                  ? "Frozen v1 source documents are unchanged."
                  : typeLabels[recordType]}
              </span>
            </div>
            <div
              className="catalog-record-list"
              data-testid="catalog-record-list"
            >
              {records.map((record) => (
                <article
                  className={`catalog-record-row ${
                    records[searchResultIndex]?.record_id === record.record_id
                      ? "is-keyboard-selected"
                      : ""
                  }`}
                  key={record.record_id}
                  data-testid={`catalog-record-${record.record_id}`}
                >
                  <div className="catalog-thumbnail">
                    {record.images?.primary_image_id ? (
                      <img
                        src={`${catalogApi}/assets/${record.images.primary_image_id}`}
                        alt=""
                      />
                    ) : (
                      <Image size={18} />
                    )}
                  </div>
                  <div className="catalog-record-identity">
                    <strong>{record.display_name || record.name}</strong>
                    <span>
                      {record.abbreviation ||
                        record.official_set_code ||
                        record.slug}
                    </span>
                  </div>
                  <div className="catalog-record-meta">
                    <span
                      className={`catalog-status-badge is-${record.status}`}
                    >
                      {record.status}
                    </span>
                    <span
                      className={
                        record.active
                          ? "catalog-active-badge"
                          : "catalog-inactive-badge"
                      }
                    >
                      {record.active ? "Active" : "Inactive"}
                    </span>
                    <span>
                      Updated {new Date(record.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="catalog-record-actions">
                    <button
                      type="button"
                      onClick={() => viewDetail(record)}
                      data-testid={`catalog-view-${record.record_id}`}
                    >
                      <Eye size={15} /> View
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(record)}
                      data-testid={`catalog-edit-${record.record_id}`}
                    >
                      <Pencil size={15} /> Edit
                    </button>
                    {!legacyMode && (
                      <button
                        type="button"
                        onClick={() => duplicate(record)}
                        data-testid={`catalog-duplicate-${record.record_id}`}
                      >
                        <Copy size={15} /> Duplicate
                      </button>
                    )}
                    {record.status === "published" ? (
                      <button
                        type="button"
                        onClick={() => changeStatus(record, "draft")}
                        data-testid={`catalog-unpublish-${record.record_id}`}
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => changeStatus(record, "published")}
                        data-testid={`catalog-publish-${record.record_id}`}
                      >
                        Publish
                      </button>
                    )}
                    {record.status === "archived" ? (
                      <button
                        type="button"
                        onClick={() => changeStatus(record, "draft")}
                        data-testid={`catalog-restore-${record.record_id}`}
                      >
                        <RotateCcw size={15} /> Restore
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => changeStatus(record, "archived")}
                        data-testid={`catalog-archive-${record.record_id}`}
                      >
                        <Archive size={15} /> Archive
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
            {!records.length && (
              <div
                className="workspace-empty"
                data-testid="catalog-empty-state"
              >
                <h2>No matching catalog records.</h2>
                <p>
                  {search
                    ? "Try a code, abbreviation, alias, or a shorter part of the name."
                    : `Create your first ${
                        singularTypeLabels[recordType] || "record"
                      } to begin this part of the catalog.`}
                </p>
                {!search && !legacyMode && (
                  <button
                    className="admin-primary-button"
                    onClick={() => openCreate(recordType)}
                    type="button"
                    data-testid="catalog-empty-create-button"
                  >
                    <Plus size={16} /> Create your first record
                  </button>
                )}
              </div>
            )}
            {!legacyMode && (
              <div className="catalog-pagination">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((current) => current - 1)}
                  data-testid="catalog-previous-page-button"
                >
                  Previous
                </button>
                <span>Page {page}</span>
                <button
                  type="button"
                  disabled={page * 25 >= total}
                  onClick={() => setPage((current) => current + 1)}
                  data-testid="catalog-next-page-button"
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>

        {user.role === "owner" && permissions && (
          <section
            className="catalog-permissions"
            data-testid="catalog-permissions-panel"
          >
            <div>
              <p className="eyebrow">Owner controls</p>
              <h2>Admin catalog permissions</h2>
            </div>
            {permissions.admins.map((admin) => (
              <article key={admin.user_id}>
                <strong>{admin.name}</strong>
                <span>{admin.email}</span>
                <div>
                  {permissions.permissions.map((permission) => (
                    <label key={permission}>
                      <input
                        type="checkbox"
                        checked={(admin.catalog_permissions || []).includes(
                          permission,
                        )}
                        onChange={(event) =>
                          updatePermissions(
                            admin,
                            permission,
                            event.target.checked,
                          )
                        }
                        data-testid={`catalog-permission-${admin.user_id}-${permission}`}
                      />{" "}
                      {permission.replaceAll("_", " ")}
                    </label>
                  ))}
                </div>
              </article>
            ))}
          </section>
        )}
      </main>

      <CatalogRecordEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSave={saveRecord}
        onNavigateRecord={openEdit}
        onNavigateType={chooseType}
        onQuickCreate={setQuickCreateContext}
        onConsumeQuickCreated={() => setQuickCreated(null)}
        onViewHistory={viewDetail}
        parentOptions={parentOptions}
        record={editingRecord}
        recordType={legacyMode ? "set" : recordType}
        quickCreated={quickCreated}
      />
      <CatalogRecordEditor
        open={Boolean(quickCreateContext)}
        onOpenChange={() => setQuickCreateContext(null)}
        onSave={saveQuickRecord}
        onNavigateRecord={openEdit}
        onNavigateType={chooseType}
        onQuickCreate={() => {}}
        onConsumeQuickCreated={() => {}}
        onViewHistory={viewDetail}
        parentOptions={parentOptions}
        record={null}
        recordType={quickCreateContext?.type || "game"}
        quickCreated={null}
      />
      <Dialog
        open={Boolean(detailRecord)}
        onOpenChange={() => setDetailRecord(null)}
      >
        <DialogContent
          className="catalog-detail-dialog"
          data-testid="catalog-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {detailRecord?.display_name || detailRecord?.name}
            </DialogTitle>
            <DialogDescription>
              Audit history and record usage.
            </DialogDescription>
          </DialogHeader>
          <div className="catalog-detail-usage">
            <ShieldCheck size={18} /> Inventory{" "}
            {detailRecord?.usage?.inventory || 0} · Catalog children{" "}
            {detailRecord?.usage?.catalog_children || 0}
          </div>
          <div className="catalog-history">
            {history.length ? (
              history.map((event) => (
                <article key={event.audit_id}>
                  <strong>{event.action}</strong>
                  <span>
                    {event.actor_name} ·{" "}
                    {new Date(event.created_at).toLocaleString()}
                  </span>
                </article>
              ))
            ) : (
              <p>No catalog changes recorded yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
